import { useMemo, useState } from 'react'
import {
  OutletDropdownListOption,
  OutletOption,
  OutletSelectProps,
} from './OutletSelect.types'

type UseOutletSelectParams = OutletSelectProps & {
  emptyLabel: string
  loadingLabel: string
}

export const hasOutletAvailability = (
  outlet: OutletOption
): outlet is OutletOption & { available: number } =>
  typeof outlet.available === 'number'

export const useOutletSelect = ({
  outlets,
  selectedOutletId,
  onOutletChange,
  disabled = false,
  isLoading = false,
  emptyLabel,
  loadingLabel,
}: UseOutletSelectParams) => {
  const [searchTerm, setSearchTerm] = useState('')

  const selectedOutlet =
    outlets.find(outlet => outlet.id === selectedOutletId) ?? outlets[0]
  const isEmpty = outlets.length === 0
  const isDisabled = disabled || isLoading || isEmpty
  const triggerOutlet = selectedOutlet ?? {
    id: '',
    name: isLoading ? loadingLabel : emptyLabel,
  }

  const filteredOutlets = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()

    if (!normalizedSearch) {
      return outlets
    }

    return outlets.filter(outlet => {
      const searchableText = `${outlet.name} ${
        outlet.address ?? ''
      }`.toLowerCase()
      return searchableText.includes(normalizedSearch)
    })
  }, [outlets, searchTerm])

  const outletById = useMemo(
    () => new Map(outlets.map(outlet => [outlet.id, outlet])),
    [outlets]
  )

  const items = useMemo<Array<{ items: OutletDropdownListOption[] }>>(
    () => [
      {
        items: filteredOutlets.map(outlet => ({
          label: outlet.name,
          value: outlet.id,
        })),
      },
    ],
    [filteredOutlets]
  )

  const handleOutletSelect = (item: OutletDropdownListOption) => {
    if (isDisabled) {
      return
    }

    const outlet = outletById.get(item.value)

    if (outlet) {
      onOutletChange(outlet)
    }
  }

  return {
    filteredOutlets,
    handleOutletSelect,
    isDisabled,
    isEmpty,
    items,
    outletById,
    searchTerm,
    selectedOutlet,
    setSearchTerm,
    triggerOutlet,
  }
}
